'use client'

import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { EVENTS } from '@configs/events.config'
import { TENANT_TYPE } from '@components/Ackodrive/common/constants'
import { useAnimateOnVisible } from '@hooks/useAnimateOnVisible'
import { CustomerVideoOptionsMock, playIcon, whiteQuote } from '@components/CustomerStory/CustomerStoryMock'
import styles from './CustomerStoryVideo.module.scss'

const CustomerStoryVideo = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const firstPlay = useRef(true)

  const { ref, animationClassName } = useAnimateOnVisible<HTMLDivElement>({ animationDelayMs: 120 })

  const play = () => {
    if (firstPlay.current) {
      void trackEvent(EVENTS[TENANT_TYPE.REVIEWS_TESTIMONIAL].engage.eventname, {
        ...EVENTS[TENANT_TYPE.REVIEWS_TESTIMONIAL].engage.eventProps,
        journey: 'video',
      })
      firstPlay.current = false
    }
    if (!playing && videoRef.current) {
      void videoRef.current.play()
      setPlaying(true)
    }
  }
  const pause = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
      setPlaying(false)
    }
  }

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
          pause()
        }
      },
      {
        threshold: [0.5],
      }
    )
    observer.observe(el)
    return () => {
      observer.unobserve(el)
    }
  }, [])

  return (
    <div
      ref={ref}
      className={`${styles.customerVideoContainer} ${animationClassName}`}
      onClick={() => playing && pause()}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video className={styles.customerVideo} ref={videoRef}>
        <source src={CustomerVideoOptionsMock.videoUrl} type="video/mp4" />
      </video>
      <div className={styles.overlay} style={{ opacity: playing ? 0 : 1 }}>
        <Image
          className={styles.customerVideoThumb}
          src={CustomerVideoOptionsMock.videoThumbnailUrl}
          alt="thumbnail"
          height={720}
          width={600}
        />
        <div className={styles.bgBlur} />
        <div className={styles.customerVideoContent}>
          <Image src={whiteQuote} alt="white-quotes" height={22} width={28} />
          <p className={styles.story}>{CustomerVideoOptionsMock.story}</p>
          <div className={styles.customerProfile}>
            <Image src={playIcon} alt="play-icon" height={48} width={48} onClick={play} />
            <div className={styles.customerProfileContent}>
              <p className={styles.customerName}>{CustomerVideoOptionsMock.customerName}</p>
              <p className={styles.customerTitle}>{CustomerVideoOptionsMock.customerTitle}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomerStoryVideo
